import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductTabsRefs
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 */

/** @extends {Component<ProductTabsRefs>} */
export class ProductTabsComponent extends Component {
  requiredRefs = ['panels'];
  #active = 0;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    const { tabs, panels } = this.refs;
    if (!panels?.length) return;
    
    // If no tabs, just show all panels (single collection mode)
    if (!tabs?.length) {
      for (const panel of panels) {
        panel.classList.add('product-tabs__panel--active');
        panel.removeAttribute('inert');
      }
      return;
    }

    // Find initial active tab
    this.#active = Math.max(
      0,
      tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')
    );

    this.#updateActiveTab();
    this.#setupEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { tabs } = this.refs;

    for (const [i, tab] of tabs.entries()) {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.#activate(i);
      }, opts);
    }
  }

  #activate(index) {
    if (index === this.#active || index < 0 || index >= this.refs.tabs.length) {
      return;
    }

    this.#active = index;
    this.#updateActiveTab();
  }

  #updateActiveTab() {
    const { tabs, panels } = this.refs;

    if (tabs?.length) {
      for (const [i, tab] of tabs.entries()) {
        const isActive = i === this.#active;
        tab.setAttribute('aria-selected', String(isActive));
        tab.classList.toggle('product-tabs__tab--active', isActive);
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      }
    }

    for (const [i, panel] of panels.entries()) {
      const isActive = i === this.#active;
      panel.classList.toggle('product-tabs__panel--active', isActive);
      panel.toggleAttribute('inert', !isActive);
      panel.setAttribute('tabindex', isActive ? '0' : '-1');
    }
  }
}

customElements.define('product-tabs-component', ProductTabsComponent);
